import type { ApiResponse } from '@shared/types'
import {
  type QueryStatus,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback } from 'react'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getEmbedSession, isEmbedMode, isHostedMode } from 'v2/shared/utils/embed-session'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { ComplianceApplicationStatus } from 'v2/types/api/settings'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { errorToast, successToast } from 'v2/utils/toasts'
import { useComplianceFormStore } from '../number-compliance/store'
import type {
  ComplianceV2RequirementsRequest,
  RV2BulkLinkResponse,
  RV2ComplianceApplication,
  RV2ComplianceApplicationDetail,
  RV2ComplianceApplications,
  RV2ComplianceCreateResponse,
  RV2ComplianceRequirement,
  RV2IndiaCaf,
  RV2IndiaCafBusinessVerificationSubmitted,
  RV2IndiaCafUpdateRequest,
} from './types-v2'

// How often to re-check an application that is still in review.
export const IN_REVIEW_POLL_MS = 10_000

// Slower after a failed check, never stopped: nothing restarts a cleared interval (window-focus
// refetch is off), so a deploy's few seconds of 5xx would freeze the status until a reload. An
// ended embed session is the exception: it can never answer again, and a hosted form outlives it.
export const inReviewPollMs = (status: QueryStatus): number | false => {
  if (isEmbedMode() && !getEmbedSession()) {
    return false
  }
  return status === 'error' ? IN_REVIEW_POLL_MS * 3 : IN_REVIEW_POLL_MS
}

export const complianceV2QueryKeys = {
  all: ['compliance-v2'] as const,
  applicationsBase: () => [...complianceV2QueryKeys.all, 'applications'] as const,
  applications: (params: Record<string, string> = {}) => [
    ...complianceV2QueryKeys.applicationsBase(),
    JSON.stringify(params),
  ],
  application: (complianceId: string) =>
    [...complianceV2QueryKeys.applicationsBase(), 'detail', complianceId] as const,
  requirements: (params: ComplianceV2RequirementsRequest) =>
    [...complianceV2QueryKeys.all, 'requirements', params] as const,
  indiaCaf: () => [...complianceV2QueryKeys.all, 'india-caf'] as const,
}

const getStatus = (error: unknown): number | undefined =>
  (error as AxiosError | undefined)?.response?.status

/**
 * Statuses that mean "render no card", as distinct from "something broke".
 *
 * 404 - the account has no CAF, the normal case for most orgs.
 * 403 - the caller's role lacks UCC View (contacto-core gates this endpoint,
 *       so finance_analyst, developer and agent are refused). Nothing has
 *       failed; they are simply not entitled to see it, and a failure card
 *       telling them to refresh would be a lie they can never resolve.
 */
const NO_CARD_STATUSES = new Set<number | undefined>([403, 404])

/**
 * India CAF for the Business Details card on Organization settings.
 *
 * `enabled` is owned by the caller (India region) so the request never fires
 * outside that gate. A 404 means the account has no CAF — the normal case for
 * most orgs, not an error — so it resolves to `null` rather than throwing, and
 * is not retried (mirrors `useUsageLimitQuery` in pages/settings/queries.ts).
 */
export const useIndiaCafQuery = (enabled: boolean) =>
  useQuery<RV2IndiaCaf | null>({
    queryKey: complianceV2QueryKeys.indiaCaf(),
    enabled,
    retry: (failureCount, error) => {
      if (NO_CARD_STATUSES.has(getStatus(error))) return false
      return failureCount < 1
    },
    queryFn: async () => {
      try {
        const response = await settingsService.getIndiaCaf()
        return response.data
      } catch (error) {
        if (NO_CARD_STATUSES.has(getStatus(error))) return null
        throw error
      }
    },
  })

// Always include end_user and documents in list responses so the table/view
// can render without additional per-row fetches.
const COMPLIANCE_LIST_EXPAND = 'end_user,documents,linked_numbers'

const withExpand = (params?: Record<string, string>): Record<string, string> => ({
  ...(params || {}),
  expand: COMPLIANCE_LIST_EXPAND,
})

/**
 * How many applications the gate query reads. An org that cannot buy a number
 * yet has, in practice, one or two applications — the cap exists so the query
 * stays cheap, and `useComplianceGateQuery`'s consumers fail OPEN when the real
 * total exceeds it rather than guessing.
 */
const GATE_QUERY_LIMIT = 20

export const complianceV2GateQueryKey = [
  ...complianceV2QueryKeys.applicationsBase(),
  'gate',
] as const

/**
 * The org's applications, newest first, for the gates that need their STATUS and
 * not just a count: India's Phone Numbers page is unusable until one is
 * approved, and the screen it shows instead depends on whether the latest is in
 * review or rejected.
 *
 * One query, one key, shared by every gate — `useIndiaComplianceFirstRun` and
 * `useKycApplicationCount` both read it, so opening the KYC form costs no extra
 * request. The key sits under `applicationsBase()`, so submitting or resubmitting
 * already invalidates it.
 *
 * It polls while anything is in review, which is what lets the gate screen flip
 * to the full page on approval without a reload.
 *
 * The hosted page shows only alias and status, so there it is read unexpanded,
 * under its own key.
 */
export const useComplianceGateQuery = (enabled = true) => {
  const hosted = isHostedMode()
  const limit = { limit: String(GATE_QUERY_LIMIT) }
  const params = hosted ? limit : withExpand(limit)

  return useQuery<{ applications: RV2ComplianceApplication[]; totalCount: number }>({
    queryKey: hosted ? [...complianceV2GateQueryKey, 'unexpanded'] : complianceV2GateQueryKey,
    enabled,
    queryFn: async () => {
      const response = (await settingsService.getComplianceApplicationsV2(params)).data

      return {
        // Sorted here rather than trusted from the API: the gate screen names the
        // LATEST application, and nothing in the response guarantees an order.
        applications: [...response.compliances].sort((a, b) =>
          (b.created_at ?? '').localeCompare(a.created_at ?? ''),
        ),
        totalCount: response.meta.total_count,
      }
    },
    refetchInterval: (query) =>
      query.state.data?.applications.some(
        (application) => application.status === ComplianceApplicationStatus.SUBMITTED,
      )
        ? inReviewPollMs(query.state.status)
        : false,
  })
}

export const COMPLIANCE_PAGE_SIZE = 20

export const useGetComplianceApplicationsV2Paginated = (
  params?: Record<string, string>,
  page = 0,
) => {
  const queryParams: Record<string, string> = {
    ...(params || {}),
    limit: String(COMPLIANCE_PAGE_SIZE),
    offset: String(page * COMPLIANCE_PAGE_SIZE),
  }
  return useQuery<{ compliances: RV2ComplianceApplication[]; totalCount: number }>({
    queryKey: complianceV2QueryKeys.applications(queryParams),
    queryFn: async () => {
      const response = await settingsService.getComplianceApplicationsV2(queryParams)
      return {
        compliances: response.data.compliances,
        totalCount: response.data.meta.total_count,
      }
    },
    // Review (OCR + validations) finishes server-side minutes after submission,
    // so a customer sitting on this table would otherwise watch a stale "In
    // review" badge until they reloaded. Poll only while something is actually
    // in review, and stop as soon as everything has settled.
    refetchInterval: (query) =>
      query.state.data?.compliances.some(
        (compliance) => compliance.status === ComplianceApplicationStatus.SUBMITTED,
      )
        ? IN_REVIEW_POLL_MS
        : false,
  })
}

const trackComplianceSubmitted = () => {
  const { formValues } = useComplianceFormStore.getState()
  const docTypesUploaded = Object.entries(formValues.uploadedFiles ?? {})
    .filter(([, file]) => Boolean(file))
    .map(([docTypeId]) => docTypeId)
  track(AnalyticsEvents.COMPLIANCE_SUBMITTED, {
    country: formValues.country_iso2,
    number_type: formValues.number_type,
    doc_types_uploaded: docTypesUploaded,
  })
}

/**
 * Page size for the Configure Number KYC select. The list API caps `limit` at
 * 100, and taking the max is deliberate: the select's search box filters the
 * options already loaded, so a bigger first page is what makes an application
 * findable by typing. Server-side `alias` search is exact-match only today
 * (number_service `alias ILIKE ?` carries no wildcards), so it cannot be wired
 * up as remote search yet.
 */
export const COMPLIANCE_SELECT_PAGE_SIZE = 100

// Under applicationsBase() so creating or updating an application already
// invalidates the select.
export const complianceV2SelectQueryKey = [...complianceV2QueryKeys.applicationsBase(), 'select']

/**
 * `useRemoteSelect` wiring for the Configure Number KYC select.
 *
 * `status` is applied server-side rather than by filtering each page after it
 * arrives, so a non-accepted application can never eat one of the page's slots.
 * `expand` is deliberately omitted — the select needs alias + compliance_id,
 * and the expanded payload carries documents and linked numbers for every row.
 *
 * Defined at module scope so the identities stay stable across renders.
 */
export const complianceV2SelectQueryConfig = {
  // Sends only what the API reads. `useRemoteSelect` also offers a `page`, and
  // forwarding it is what caused this bug on the list table: the endpoint
  // silently ignores `page` and pages by `offset` alone.
  fetchService: ({ limit, offset }: Record<string, unknown>) =>
    settingsService.getComplianceApplicationsV2({
      limit: String(limit ?? COMPLIANCE_SELECT_PAGE_SIZE),
      offset: String(offset ?? 0),
      status: ComplianceApplicationStatus.ACCEPTED,
    }),
  optionExtractor: (data: unknown) => {
    const { compliances, meta } = data as RV2ComplianceApplications
    return {
      options: (compliances || []).map((application) => ({
        label: application.alias,
        value: application.compliance_id,
      })),
      hasNextPage: Boolean(meta?.next),
    }
  },
  // The linked application is resolved by id when it falls outside the loaded
  // pages — including when it is no longer `accepted` and so is filtered out of
  // the list entirely. Without this the select renders blank on those numbers,
  // which reads as "no KYC" and invites unlinking a live application.
  fetchResource: (complianceId: string | number) =>
    settingsService.getComplianceApplicationV2(String(complianceId)),
  optionFromResource: (data: unknown) => {
    const { compliance } = data as RV2ComplianceApplicationDetail
    return { label: compliance.alias, value: compliance.compliance_id }
  },
}

// Query config for RemoteTable
export const complianceV2ApplicationsQueryConfig = {
  fetchService: async (params: Record<string, string>) => {
    const response = await settingsService.getComplianceApplicationsV2(withExpand(params))
    return response
  },
  // In offset mode the pager's Next/Previous buttons are driven by these two
  // flags, not by the row count — `useDataTable` overrides getCanNextPage /
  // getCanPreviousPage with them, and they default to false. Omitting them
  // leaves the buttons permanently disabled.
  extractData: (data: RV2ComplianceApplications) => ({
    data: data.compliances,
    totalCount: data.meta.total_count,
    hasNext: Boolean(data.meta.next),
    hasPrevious: Boolean(data.meta.previous),
  }),
}

// Create Compliance Application (single multipart POST)
export function useCreateComplianceApplicationV2() {
  const queryClient = useQueryClient()

  return useMutation<RV2ComplianceCreateResponse, AxiosError<ApiResponse<unknown>>, FormData>({
    mutationFn: async (formData) => {
      const response = await settingsService.createComplianceApplicationV2(formData)
      return response.data
    },
    onSuccess: () => {
      // The hosted page has its own status screen.
      if (!isHostedMode()) {
        successToast({
          title: 'Application submitted',
          description:
            "We are reviewing your details and will email you once it's done. This usually takes under 5 minutes.",
        })
      }
      queryClient.invalidateQueries({ queryKey: complianceV2QueryKeys.applicationsBase() })
      trackComplianceSubmitted()
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to create KYC',
        description: getErrorText(error),
      })
    },
  })
}

/** Keyed so every mounted Update button shares one pending state. */
export const indiaCafUpdateMutationKey = ['india-caf-update'] as const

/**
 * The new document in ONE call: core stores it as a draft application and
 * forwards it, never submitting. Replaces create-then-request, which filed a
 * KYC submission as a side effect and ran the compliance workflow too.
 */
export function useUploadIndiaCafUpdate() {
  const queryClient = useQueryClient()

  return useMutation<RV2IndiaCafUpdateRequest, AxiosError<ApiResponse<unknown>>, FormData>({
    mutationKey: indiaCafUpdateMutationKey,
    mutationFn: async (formData) => {
      const response = await settingsService.uploadIndiaCafUpdate(formData)
      return response.data
    },
    onSuccess: () => {
      successToast({
        title: 'Document submitted',
        description:
          "We'll review it and email you the outcome shortly, whether it's approved or rejected.",
      })
      queryClient.invalidateQueries({ queryKey: complianceV2QueryKeys.applicationsBase() })
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to submit the update',
        description: getErrorText(error),
      })
    },
  })
}

/** Keyed so the upload button's loader reads the mutation cache, not one hook's isPending. */
export const businessVerificationSubmitMutationKey = ['india-caf-business-verification'] as const

/**
 * Uploads the signed and stamped yearly verification form. The review is manual,
 * so success moves the verification to in review; the CAF refetch shows that.
 */
export function useSubmitBusinessVerification() {
  const queryClient = useQueryClient()

  return useMutation<
    RV2IndiaCafBusinessVerificationSubmitted,
    AxiosError<ApiResponse<unknown>>,
    FormData
  >({
    mutationKey: businessVerificationSubmitMutationKey,
    mutationFn: async (formData) => {
      const response = await settingsService.submitIndiaCafBusinessVerification(formData)
      return response.data
    },
    onSuccess: () => {
      successToast({
        title: 'Signed form submitted',
        description:
          'We will review it within 3 to 5 business days and email you once the review is complete.',
      })
      queryClient.invalidateQueries({ queryKey: complianceV2QueryKeys.indiaCaf() })
    },
    onError: (error) => {
      errorToast({ title: 'Failed to submit the form', description: getErrorText(error) })
    },
  })
}

export const useIsBusinessVerificationSubmitting = () =>
  useIsMutating({ mutationKey: businessVerificationSubmitMutationKey }) > 0

// Update Compliance Application (POST to /update endpoint)
export function useUpdateComplianceApplicationV2() {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    AxiosError<ApiResponse<unknown>>,
    { complianceId: string; formData: FormData }
  >({
    mutationFn: async ({ complianceId, formData }) => {
      const response = await settingsService.updateComplianceApplicationV2(complianceId, formData)
      return response.data
    },
    onSuccess: () => {
      if (!isHostedMode()) {
        successToast({
          title: 'Application updated',
          description:
            "We are reviewing your details and will email you once it's done. This usually takes under 5 minutes.",
        })
      }
      queryClient.invalidateQueries({ queryKey: complianceV2QueryKeys.applicationsBase() })
      trackComplianceSubmitted()
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to update KYC',
        description: getErrorText(error),
      })
    },
  })
}

const deleteComplianceApplication = async (complianceId: string) => {
  return settingsService.deleteComplianceApplicationV2({ complianceId })
}

export const useDeleteComplianceApplicationV2 = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteComplianceApplication,
    onSuccess: () => {
      successToast({
        title: 'Application Deleted',
        description: 'Your KYC has been deleted successfully.',
      })
      queryClient.invalidateQueries({ queryKey: complianceV2QueryKeys.applicationsBase() })
    },
    onError: (error: AxiosError<ApiResponse<unknown>>) => {
      const responseData = error.response?.data
      const errors = responseData?.errors

      if (errors?.global_error && typeof errors.global_error === 'string') {
        errorToast({
          title: 'Failed to delete KYC',
          description: errors.global_error,
        })
        return
      }
      errorToast({
        title: 'Failed to delete KYC',
        description: getErrorText(error),
      })
    },
  })
}

// Imperative fetch for compliance requirements
export function useFetchComplianceRequirementsV2() {
  const queryClient = useQueryClient()

  return useCallback(
    async (params: ComplianceV2RequirementsRequest): Promise<RV2ComplianceRequirement> =>
      queryClient.fetchQuery({
        queryKey: complianceV2QueryKeys.requirements(params),
        queryFn: async () => {
          const response = await settingsService.getComplianceRequirementsV2(params)
          return response.data
        },
      }),
    [queryClient],
  )
}

// Link numbers to compliance applications
export function useLinkComplianceNumbers() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      numbers: Array<{ number: string; compliance_application_id: string }>
    }) => {
      const response = await settingsService.linkComplianceNumbers(payload)
      return response.data as RV2BulkLinkResponse
    },
    onSuccess: (data) => {
      // Invalidate when at least one number was updated (handles partial success)
      if (data?.updated_count) {
        queryClient.invalidateQueries({ queryKey: complianceV2QueryKeys.applicationsBase() })
        // Phone number compliance_application_id changed — refresh the numbers list
        queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
      }

      const failedEntries = data?.report?.filter((r) => r.status === 'failed') || []
      if (failedEntries.length > 0) {
        errorToast({
          title: 'Failed to link numbers',
          description: failedEntries[0].remarks || 'Unable to link numbers. Please try again.',
        })
        return
      }
      successToast({
        title: 'Numbers linked',
        description: 'Numbers were linked to the KYC.',
      })
    },
    onError: (error: AxiosError<ApiResponse<unknown>>) => {
      errorToast({
        title: 'Failed to link numbers',
        description: getErrorText(error),
      })
    },
  })
}
